/**
 * Catch-all utility helpers — object manipulation, URL parsing, deployment
 * config, type conversions, and a few hooks.
 *
 * Not a coherent API: each function stands alone and was added when a generic
 * helper was needed somewhere in the codebase. Look here first when you need a
 * small primitive, but feel free to leave new helpers next to their first
 * caller when they're domain-specific. Two functions worth knowing by name:
 * `getDeploymentConfig` resolves the active deployment from `psi.config` based
 * on the runtime domain (falling back to production for unknown hosts), and
 * `invariant` throws in dev/test but only warns in production — use it for
 * design-invariant checks that shouldn't crash users.
 */

import { getIsLocalhost } from 'platform-specific/url';
import { deploymentConfigMap, domainToDeploymentMap } from 'psi.config';
import { useState } from 'react';
import type { DeploymentConfig } from 'util/stdtypes';

export { formatString } from '@repo/shared/translations/label';

const deploymentConfig = getDeploymentConfig();

export function toBool(arg: any): boolean {
    if (arg) {
        return true;
    } else {
        return false;
    }
}

export function stripSuffix(str: string, suffix: string): string {
    if (str.endsWith(suffix)) {
        return str.substring(0, str.length - suffix.length);
    } else {
        return str;
    }
}

export function generateRandomKey(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const randomArray = new Uint32Array(length);
    window.crypto.getRandomValues(randomArray);

    let result = '';
    for (let i = 0; i < length; i++) {
        const randomIndex = randomArray[i] % characters.length;
        result += characters.charAt(randomIndex);
    }

    return result;
}

export function boolToString(bool: boolean): string {
    return bool ? 'true' : 'false';
}

export function makeAssetUrl(urlSuffix: string): string {
    if (urlSuffix.startsWith('http://') || urlSuffix.startsWith('https://')) {
        return urlSuffix;
    } else {
        return getBaseUrl() + urlSuffix;
    }
}

export function getFirstName(name: string | null): string {
    return (name || '').trim().split(' ')[0];
}

export async function pauseAsync(milliseconds: number) {
    await new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export function removeUndefinedFields(obj: Record<string, any>): Record<string, any> {
    const clone = { ...obj };
    for (const key in clone) {
        if (clone[key] === undefined) {
            delete clone[key];
        }
    }
    return clone;
}

export function keysToMap(keys: string[], func: (key: string) => any = () => true): Record<string, any> {
    const map: Record<string, any> = {};
    keys.forEach((key) => {
        map[key] = func(key);
    });
    return map;
}

export function assembleUrl(baseUrl: string, queryParams: Record<string, string>): string {
    const url = new URL(baseUrl);
    Object.keys(queryParams).forEach((key) => url.searchParams.append(key, queryParams[key]));
    return url.toString();
}

export function makeRandomNonce(): string {
    if (process.env.NODE_ENV === 'test') {
        return 'testnonce';
    } else {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }
}

export function getDomain(): string {
    if (process.env.NODE_ENV === 'test') {
        return 'test';
    } else if (getIsLocalhost()) {
        return 'localhost';
    } else {
        return window.location.hostname;
    }
}

export function getDeploymentConfig(): DeploymentConfig {
    const domain = getDomain();
    // Special behavior for Firebase preview environments.
    let deployment: string;
    if (domain.startsWith('np-psi-staging--')) {
        deployment = 'preview';
    } else {
        deployment = domainToDeploymentMap[domain];
    }
    if (!deployment) {
        console.error(
            `psi.config.js: domain ${domain} not found in domainToDeploymentMap. Using production config as fallback.\n`,
            'Available mappings:',
            domainToDeploymentMap,
        );
        return deploymentConfigMap.production;
    } else if (!deploymentConfigMap[deployment]) {
        console.error(
            `psi.config.js: deployment ${deployment} not found in deploymentConfigMap. Using production config as fallback.\n`,
            'Available configs:',
            deploymentConfigMap,
        );
        return deploymentConfigMap.production;
    }
    return deploymentConfigMap[deployment];
}

export function getBaseUrl(): string {
    return deploymentConfig.baseUrl ?? `https://${getDomain()}/${deploymentConfig.basePath ?? ''}`;
}

export function getApiBaseUrl(): string {
    return deploymentConfig.apiBaseUrl ?? `${getBaseUrl()}api/`;
}

// e.g. basePath '/'              → /siloKey/foo  → /siloKey/foo
//      basePath 'conversations/' → /conv/siloKey → /siloKey
//      basePath 'conv/test/'     → /conv/test/siloKey/foo → /siloKey/foo
function stripBasePath(pathname: string): string {
    const basePath = deploymentConfig.basePath ?? '/';
    if (basePath === '/') {
        return pathname;
    }
    const prefix = `/${basePath}`;
    return pathname.startsWith(prefix) ? `/${pathname.slice(prefix.length)}` : pathname;
}

export function parsePsiPath(pathname: string): { siloKey: string | undefined; rest: string[] } {
    const [siloKey, ...rest] = stripBasePath(pathname).split('/').filter(Boolean);
    return { siloKey, rest };
}

export function getErrorMonitoringDsn(): string | undefined {
    return deploymentConfig.errorMonitoringDsn ?? undefined;
}

export function getIsInIOS(window: any): boolean {
    return !!window?.webkit?.messageHandlers;
}

export function getIsInAndroid(window: any): boolean {
    return !!window?.Android;
}

export function getIsInIFrame(window: any): boolean {
    return window?.self !== window?.top;
}

/**
 * Validates a condition and throws in dev/test or logs in production. Use for
 * design invariant checks that shouldn't crash production.
 */
export function invariant(condition: boolean, message: string): void {
    if (condition) return;
    if (process.env.NODE_ENV === 'production') {
        console.warn(message);
    } else {
        throw new Error(message);
    }
}

export function toTitleCase(str: string): string {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function kebabCaseToTitleCase(str: string): string {
    return str
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();
}

export function camelCaseToTitleCase(str: string): string {
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();
}

export function isValidUrl(url: string): boolean {
    try {
        const urlObj = new URL(url);
        return ['http:', 'https:'].includes(urlObj.protocol);
    } catch (error) {
        return false;
    }
}

// Returns a value computed exactly once on mount and kept stable for the life
// of the component. Implemented via `useState`'s init-function form, which
// React guarantees calls `init` exactly once. Prefer this over
// `useMemo(init, [])` (no stability guarantee) and over
// `useRef(init()).current` (re-evaluates `init()` every render, throwing away
// the result).
export function useConstant<T>(init: () => T): T {
    const [value] = useState<T>(init);
    return value;
}
